// c:\xampp\htdocs\miners-market-main\components\forms\MediaManager.js

import React from 'react';

const DOC_CATEGORIES = {
    'manual': 'User Manual',
    'datasheet': 'Data Sheet',
    'rebuild_datasheet': 'Data Pack File',
    'conformity': 'Certificate of Conformity',
    'calibration': 'Calibration Certificate',
    'warranty': 'Warranty Card',
    'service_history': 'Service History',
    'factory_test': 'Factory Test Report',
    'oil_report': 'Oil Analysis Report'
};

// --- NEW: Internal Component for Robust Image Loading ---
const ImageWithFallback = ({ src, alt, className }) => {
    const [error, setError] = React.useState(false);

    // If source changes, reset error state
    React.useEffect(() => {
        setError(false);
    }, [src]);

    if (error || !src) {
        return (
            <div className={`flex flex-col items-center justify-center bg-gray-100 text-gray-400 ${className}`} title={src || "No source"}>
                <i className="fas fa-image text-3xl mb-1"></i>
                <span className="text-[10px] text-center px-1">No Preview</span>
            </div>
        );
    }

    return (
        <img
            src={src} // The actual image
            alt={alt}
            className={className}
            onError={(e) => {
                console.warn("Image load failed:", src);
                setError(true);
            }}
            loading="lazy"
        />
    );
};

function MediaManager({ formData, onFormChange, isEdit = false }) {
    const fileInputRef = React.useRef(null);
    const docInputRef = React.useRef(null);
    const [uploading, setUploading] = React.useState(false);
    const [error, setError] = React.useState(null);
    
// Internal state to manage the list of media items
    const [mediaItems, setMediaItems] = React.useState([]);

    // --- UPDATED: Robust Path Cleaner ---
    const cleanPath = (path) => {
        if (!path) return null;
        // Allow external links and data URIs (base64 previews)
        if (path.match(/^(http|https|data):/i)) return path;

        // Normalize backslashes to forward slashes
        let clean = path.replace(/\\/g, '/').trim();
        
        // If path is already root-relative to the server (starts with /miners-market-main/), keep it
        if (clean.startsWith('/miners-market-main/')) {
            // FIX: Ensure 'uploads' segment exists for product images
            if (clean.includes('/images/product_') && !clean.includes('/uploads/')) {
                return clean.replace('/images/', '/uploads/images/');
            }
            return clean;
        }

        // Strategy: Locate 'uploads/' or 'images/' to anchor the path
        // This handles cases like "C:/xampp/htdocs/miners-market-main/uploads/..." 
        // or just "uploads/..."
        const anchorMatch = clean.match(/(uploads\/|images\/|miscellaneous items\/)/i);
        
        if (anchorMatch) {
            // Keep everything starting from "uploads/", "images/", etc.
            clean = clean.substring(anchorMatch.index);
        }
        
        // Ensure absolute path starting with /
        if (!clean.startsWith('/')) {
            clean = '/' + clean;
        }
        
        // FIX: Ensure 'uploads' prefix if missing for product images
        if (clean.startsWith('/images/product_')) {
            clean = '/uploads' + clean;
        }

        // Return the clean absolute path
        return encodeURI(clean);
    };

    // --- Initialization Logic ---
    React.useEffect(() => {
        if (formData.media && Array.isArray(formData.media)) {
            setMediaItems(formData.media.map(item => ({ ...item, file_path: cleanPath(item.file_path) })));
        } else {
            const initialMedia = [];
            
            // 1. Recover Images from legacy columns
            for (let i = 1; i <= 25; i++) { // Allow up to 25 images
                const url = formData[`image_url_${i}`];
                if (url) {
                    initialMedia.push({
                        media_type: 'image',
                        category: 'gallery', 
                        file_path: url,
                        sort_order: i,
                        display_name: cleanPath(url).split('/').pop()
                    });
                }
            }

            // 2. Recover Standard Documents
            const docFields = [
                { key: 'doc_manual_url', cat: 'manual', label: 'User Manual' },
                { key: 'doc_datasheet_url', cat: 'datasheet', label: 'Data Sheet' },
                { key: 'doc_conformity_url', cat: 'conformity', label: 'Certificate of Conformity' },
                { key: 'doc_calibration_url', cat: 'calibration', label: 'Calibration Certificate' },
                { key: 'doc_warranty_url', cat: 'warranty', label: 'Warranty Card' },
                { key: 'doc_service_history_url', cat: 'service_history', label: 'Service History' },
                { key: 'doc_factory_test_url', cat: 'factory_test', label: 'Factory Test Report' },
                { key: 'doc_oil_report_url', cat: 'oil_report', label: 'Oil Analysis Report' }
            ];

            docFields.forEach(doc => {
                if (formData[doc.key]) {
                    initialMedia.push({
                        media_type: 'document',
                        category: doc.cat,
                        file_path: formData[doc.key],
                        sort_order: 0,
                        display_name: cleanPath(formData[doc.key]).split('/').pop()
                    });
                }
            });

            // 3. Recover Rebuild Datasheets (1-25)
            for (let i = 1; i <= 25; i++) {
                const url = formData[`doc_datasheet_${i}_url`];
                if (url) {
                    initialMedia.push({
                        media_type: 'document',
                        category: 'rebuild_datasheet',
                        file_path: url,
                        sort_order: i,
                        display_name: cleanPath(url).split('/').pop(),
                    });
                }
            }

            setMediaItems(initialMedia);
            // Sync initial state back to parent immediately
            onFormChange({ target: { name: 'media', value: initialMedia } });
        }
    }, []); // Run only once on mount

    // --- Sync legacy image_url_X fields from formData into mediaItems ---
    React.useEffect(() => {
        const legacyImages = [];
        for (let i = 1; i <= 25; i++) {
            const url = formData[`image_url_${i}`];
            if (url) {
                // Check if this URL is already in mediaItems
                const exists = mediaItems.some(m => m.media_type === 'image' && m.file_path === url);
                if (!exists) {
                    legacyImages.push({
                        media_type: 'image',
                        category: 'gallery',
                        file_path: url,
                        sort_order: i,
                        display_name: cleanPath(url).split('/').pop()
                    });
                }
            }
        }
        if (legacyImages.length > 0) {
            const updatedMedia = [...mediaItems, ...legacyImages];
            setMediaItems(updatedMedia);
            onFormChange({ target: { name: 'media', value: updatedMedia } });
        }
    }, [formData]);

    // --- Upload Handler ---
    const handleUpload = async (e, type, category = 'gallery') => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        // --- NEW: Client-side file size validation (100MB max) ---
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        const oversizedFiles = files.filter(f => f.size > maxSize);
        if (oversizedFiles.length > 0) {
            setError(`File(s) exceed 100MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
            if (e.target) e.target.value = '';
            return;
        }

        setUploading(true);
        setError(null);

        const newItems = [];

        try {
            for (const file of files) {
                const uploadData = new FormData();
                uploadData.append('file', file);
                uploadData.append('type', type); 

                const response = await fetch('/api/upload_files.php', {
                    method: 'POST',
                    body: uploadData,
                    credentials: 'include'
                });
                
                const result = await response.json();
                if (!result.success) throw new Error(result.message || "Upload failed");

                const uploadedUrl = result.url || (result.urls ? Object.values(result.urls)[0] : null);
                if (!uploadedUrl) throw new Error("Upload succeeded but no URL returned");

                // Calculate sort order
                let nextOrder = 0;
                if (type === 'image') {
                    const currentImages = mediaItems.filter(m => m.media_type === 'image');
                    nextOrder = currentImages.length + newItems.length + 1;
                } else if (category === 'rebuild_datasheet') {
                    const currentDatasheets = mediaItems.filter(m => m.category === 'rebuild_datasheet');
                    // Find max sort_order among existing datasheets plus any we've just added in this batch
                    const maxOrder = currentDatasheets.concat(newItems).reduce((max, item) => Math.max(max, item.sort_order || 0), 0);
                    nextOrder = maxOrder + 1;
                }

                newItems.push({
                    media_type: type === 'image' ? 'image' : 'document',
                    category: category,
                    file_path: uploadedUrl,
                    sort_order: nextOrder,
                    display_name: file.name
                });
            }

            const updatedMedia = [...mediaItems, ...newItems];
            setMediaItems(updatedMedia);
            onFormChange({ target: { name: 'media', value: updatedMedia } });

        } catch (err) {
            console.error("Upload error:", err);
            setError(err.message);
        } finally {
            setUploading(false);
            if (e.target) e.target.value = ''; 
        }
    };

    const handleRemove = (index) => {
        const updatedMedia = mediaItems.filter((_, i) => i !== index);
        
        // Re-index images to ensure sort_order remains sequential (1, 2, 3...)
        let imgCounter = 1;
        const reindexedMedia = updatedMedia.map(item => {
            if (item.media_type === 'image') {
                return { ...item, sort_order: imgCounter++ };
            }
            return item;
        });

        setMediaItems(reindexedMedia);
        onFormChange({ target: { name: 'media', value: reindexedMedia } });
    };

    const images = mediaItems.filter(m => m.media_type === 'image').sort((a, b) => a.sort_order - b.sort_order);
    const documents = mediaItems.filter(m => m.media_type === 'document');

    return (
        <div className="space-y-6">
            {error && <div className="bg-red-100 text-red-700 p-3 rounded text-sm">{error}</div>}

            {/* --- IMAGES SECTION --- */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-bold text-gray-700">Gallery Images ({images.length})</label>
                    <button 
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                        disabled={uploading}
                        className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm flex items-center"
                    >
                        {uploading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Uploading...</> : <><i className="fas fa-plus mr-2"></i>Add Images</>}
                    </button>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                            className="hidden"
                        multiple 
                            accept="image/*" 
                        onChange={(e) => handleUpload(e, 'image')} 
                    />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {images.map((img, idx) => (
                        <div key={idx} className="relative group aspect-square bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors">
                            {/* --- UPDATED: Use the new ImageWithFallback component --- */}
                            <ImageWithFallback 
                                src={cleanPath(img.file_path)} 
                                alt={`Image ${img.sort_order}`}
                                className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-105"
                            />
                            
                            {/* Overlay for actions and info */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                                <div className="flex justify-end">
                                    <button 
                                        type="button"
                                        onClick={() => handleRemove(mediaItems.indexOf(img))}
                                        className="bg-red-500 hover:bg-red-600 text-white w-7 h-7 rounded-full flex items-center justify-center shadow-sm transition-transform hover:scale-110"
                                        title="Remove Image"
                                    >
                                        <i className="fas fa-trash-alt text-xs"></i>
                                    </button>
                                </div>
                                <span className="self-start bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-mono backdrop-blur-sm">
                                    #{img.sort_order}
                                </span>
                            </div>
                        </div>
                    ))}
                    
                    {/* Add Button as a Card if list is empty or for easy access */}
                    {images.length === 0 && (
                        <div 
                            className="col-span-2 sm:col-span-3 md:col-span-5 flex flex-col items-center justify-center py-10 text-gray-400 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                            onClick={() => fileInputRef.current.click()}
                        >
                            <i className="fas fa-cloud-upload-alt text-3xl mb-2"></i>
                            <p className="text-sm font-medium">No images uploaded yet.</p>
                            <p className="text-xs">Click to upload or drag and drop</p>
                        </div>
                    )}
                </div>
            </div>

            {/* --- DOCUMENTS SECTION (Unchanged) --- */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-bold text-gray-700">Documents ({documents.length})</label>
                    <div className="flex gap-2">
                        <select 
                            className="text-sm border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                            onChange={(e) => { if(e.target.value) { docInputRef.current.setAttribute('data-category', e.target.value); docInputRef.current.click(); e.target.value = ""; } }}
                        >
                            <option value="">+ Add Document...</option>
                            {Object.entries(DOC_CATEGORIES).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                        <input 
                            type="file" 
                            ref={docInputRef} 
                            className="hidden" 
                            multiple
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={(e) => handleUpload(e, 'document', docInputRef.current.getAttribute('data-category'))} 
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    {documents.map((doc, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white p-3 rounded border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="bg-blue-100 p-2 rounded text-blue-600 flex-shrink-0">
                                    <i className="fas fa-file-alt"></i>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate" title={doc.display_name}>
                                        <span className="text-gray-600 mr-1 font-normal">{DOC_CATEGORIES[doc.category] || doc.category}:</span> 
                                        {doc.display_name}
                                    </p>
                                    <a href={cleanPath(doc.file_path)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:text-blue-700 hover:underline truncate block">
                                        <i className="fas fa-external-link-alt mr-1"></i> View File
                                    </a>
                                </div>
                            </div>
                            <button 
                                type="button"
                                onClick={() => handleRemove(mediaItems.indexOf(doc))}
                                className="text-gray-400 hover:text-red-500 p-2 transition-colors rounded-full hover:bg-red-50"
                                title="Remove Document"
                            >
                                <i className="fas fa-trash-alt"></i>
                            </button>
                        </div>
                    ))}
                    {documents.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">No documents uploaded.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MediaManager;
